import {
  BenefitItemVariant,
  DecoratedInsurancePlan,
  InsuranceBenefitService,
  InsurancePlan,
  InsurancePlanBenefitItem,
  InsurancePlanType,
} from '@typesApp/InsurancePlan'

// allow plans to select preferred services
// instead of just the first service
const PREFERRED_SERVICE_REGEX = {
  CONTACT_LENSES: /contacts[\s-]*disposable/gi,
} as Record<InsurancePlanType, RegExp | null>

// note the FRAME_WITH_LENSES is a pseudo plan type used to
// denote either a frame or lenses plan that is into additional purchase
const DEFAULT_DISPLAY_ORDER: InsurancePlanType[] = [
  'FRAME',
  'LENSES',
  'CONTACT_LENSES',
  'FRAME_WITH_LENSES',
  'EYE_EXAM',
]

const getDisplayOrder = (displayOrder: InsurancePlanType[] | undefined): InsurancePlanType[] => {
  if (!displayOrder || !Array.isArray(displayOrder) || !displayOrder.length) {
    return DEFAULT_DISPLAY_ORDER
  }

  // current configuration does not have FRAME_WITH_LENSES
  // until we update configuration, we need to insert this
  // before EYE_EXAM
  const mergedDisplayOrder = [...displayOrder]

  const indexOfEyeExam = displayOrder.indexOf('EYE_EXAM')
  if (indexOfEyeExam === -1) {
    mergedDisplayOrder.splice(displayOrder.length - 1, 0, 'FRAME_WITH_LENSES')
  } else {
    mergedDisplayOrder.splice(indexOfEyeExam, 0, 'FRAME_WITH_LENSES')
  }
  return mergedDisplayOrder
}

const getInsurancePlan = (
  insurancePlans: InsurancePlan[] | undefined,
  planType: InsurancePlanType
): InsurancePlan | null => {
  if (!Array.isArray(insurancePlans)) return null
  return insurancePlans.find(plan => plan.planType === planType && plan.planBenefits?.some(isValidBenefit)) ?? null
}

/**
 * @returns array of decorated plans ordered according to the provided display order (or default order)
 */
export const getDecoratedInsurancePlans = (
  plans: InsurancePlan[],
  configuredDisplayOrder: InsurancePlanType[] | undefined
): DecoratedInsurancePlan[] => {
  const displayOrder = getDisplayOrder(configuredDisplayOrder)

  // check if we have frame + lenses AP
  // any frame or lenses with AP is considered as FRAME_WITH_LENSES
  const framesPlan = getInsurancePlan(plans, 'FRAME')
  const lensesPlan = getInsurancePlan(plans, 'LENSES')
  const frameWithLensesPlan =
    hasAdditionalPurchase(framesPlan) || hasAdditionalPurchase(lensesPlan) ? (framesPlan ?? lensesPlan) : null

  const result: DecoratedInsurancePlan[] = []

  // build list of decorated plans according to display order
  displayOrder.forEach(planType => {
    let plan = getInsurancePlan(plans, planType)
    let variant: BenefitItemVariant = 'routine'

    // handle pseudo planType for frame + lenses additional purchase
    if (!plan && planType === 'FRAME_WITH_LENSES' && frameWithLensesPlan) {
      plan = frameWithLensesPlan
      variant = 'additional-purchase'
    }

    if (plan) {
      result.push({
        plan,
        displayPlanType: planType,
        variant,
      })
    }
  })

  return result
}

const getActiveBenefitByCategory = (
  plan: InsurancePlan,
  category: InsurancePlanBenefitItem['benefitCategory']
): InsurancePlanBenefitItem | undefined => {
  const planBenefits = (plan?.planBenefits ?? []).filter(isValidBenefit)

  const availableBenefit = planBenefits.find(
    benefit => benefit.benefitCategory === category && benefit.benefitStatus === 'BENEFITS_AVAILABLE'
  )

  const isBenefitAvailable = Boolean(availableBenefit)
  let activeBenefit = availableBenefit

  if (!isBenefitAvailable) {
    const futureBenefit = planBenefits.find(
      benefit =>
        benefit.benefitCategory === category &&
        benefit.benefitStatus === 'BENEFITS_NOT_AVAILABLE' &&
        Boolean(benefit.benefitRenewalDate)
    )
    activeBenefit = futureBenefit
  }

  return activeBenefit
}

export const getRoutineActiveBenefit = (plan: InsurancePlan): InsurancePlanBenefitItem | undefined => {
  return getActiveBenefitByCategory(plan, 'ROUTINE')
}

export const getAdditionalPurchaseActiveBenefit = (plan: InsurancePlan): InsurancePlanBenefitItem | undefined => {
  return getActiveBenefitByCategory(plan, 'ADDITIONAL_PURCHASE')
}

export const getBenefitService = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): InsuranceBenefitService | undefined => {
  if (!planType || !benefitItem?.services) return

  let service = benefitItem.services?.[0]

  // return preferred service if available (as for contact lens) or first one
  const regex = PREFERRED_SERVICE_REGEX[planType]
  if (regex) {
    service = benefitItem.services?.find(service => service?.serviceName?.match(regex)) ?? service
  }

  return service
}

export const isValidBenefit = (benefit: InsurancePlanBenefitItem): boolean => {
  return (
    benefit.benefitAvailability === 'SUPPORTED' ||
    benefit.benefitAvailability === 'COMING_SOON' ||
    benefit.benefitAvailability === 'DISPLAY_ONLY' ||
    benefit.benefitAvailability === 'CUSTOMER_SERVICE_ONLY'
  )
}

export const isBenefitAvailable = (benefit: InsurancePlanBenefitItem | undefined): boolean => {
  return (
    benefit?.benefitAvailability === 'SUPPORTED' ||
    benefit?.benefitAvailability === 'DISPLAY_ONLY' ||
    benefit?.benefitAvailability === 'CUSTOMER_SERVICE_ONLY'
  )
}

export const hasAdditionalPurchase = (insurancePlan: InsurancePlan | undefined | null): boolean => {
  if (!insurancePlan || !Array.isArray(insurancePlan.planBenefits) || !insurancePlan.planBenefits?.some(isValidBenefit))
    return false

  // get ADDITIONAL_PURCHASE items that are supported and available
  const supportedItems = insurancePlan.planBenefits.filter(
    item =>
      item.benefitCategory === 'ADDITIONAL_PURCHASE' &&
      (item.benefitAvailability === 'SUPPORTED' || item.benefitAvailability === 'CUSTOMER_SERVICE_ONLY') &&
      item.benefitStatus === 'BENEFITS_AVAILABLE'
  )

  return supportedItems.length > 0
}

export const checkBenefitServiceHasCopay = (service: InsuranceBenefitService | undefined): boolean => {
  const text = service?.serviceDescription
  if (!text) return false
  const copayVersions = ['copay', 'co-pay', 'co pay']
  return copayVersions.some(ver => text.toLowerCase().includes(ver))
}

type BenefitServiceDescriptionInfo = {
  isLenses: boolean
  isFrame: boolean
  isEyeExam: boolean
  isContacts: boolean
  isCopay: boolean
  isComingSoon: boolean
  isStatusAvailable: boolean
  serviceDescription: string | undefined
  showFrameFallbackDescription: boolean
  showLensesFallbackDescription: boolean
  benefitService: InsuranceBenefitService | undefined
}

export const getBenefitServiceDescriptionInfo = (
  planType: InsurancePlanType,
  benefitItem: InsurancePlanBenefitItem | undefined
): BenefitServiceDescriptionInfo => {
  const isStatusAvailable = benefitItem?.benefitStatus === 'BENEFITS_AVAILABLE'
  const isComingSoon = benefitItem?.benefitAvailability === 'COMING_SOON'
  const benefitService = getBenefitService(planType, benefitItem)
  const isCopay = checkBenefitServiceHasCopay(benefitService)

  const isEyeExam = planType === 'EYE_EXAM'
  const isLenses = planType === 'LENSES'
  const isFrame = planType === 'FRAME'
  const isContacts = planType === 'CONTACT_LENSES'

  const serviceDescription = !isComingSoon ? benefitService?.serviceDescription : undefined
  const showFrameFallbackDescription = isStatusAvailable && isFrame && !benefitService
  const showLensesFallbackDescription =
    isStatusAvailable && isLenses && (benefitItem?.services?.length > 1 || !benefitService)

  return {
    isLenses,
    isFrame,
    isEyeExam,
    isContacts,
    isCopay,
    isComingSoon,
    isStatusAvailable,
    serviceDescription,
    showFrameFallbackDescription,
    showLensesFallbackDescription,
    benefitService,
  }
}
